import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cycleApi, ratingApi } from '../../services/api';
import { BandBadge, Spinner, StatCard } from '../../components/shared/index';
import toast from 'react-hot-toast';
import { ChartBarIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

export default function CycleDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: cycle, isLoading } = useQuery({ queryKey: ['cycle', id], queryFn: () => cycleApi.get(id).then(r => r.data) });
  const { data: summary } = useQuery({ queryKey: ['cycle-summary', id], queryFn: () => cycleApi.summary(id).then(r => r.data) });

  const lockMut    = useMutation({ mutationFn: () => ratingApi.lock(id), onSuccess: () => { qc.invalidateQueries(['cycle',id]); toast.success('Ratings locked'); } });
  const publishMut = useMutation({ mutationFn: () => ratingApi.publish(id), onSuccess: () => { qc.invalidateQueries(['cycle',id]); toast.success('Published to employees'); } });

  const handleExport = () => cycleApi.export(id).then(r => {
    const url = URL.createObjectURL(new Blob([r.data]));
    const a   = document.createElement('a'); a.href = url; a.download = `calibza-${id}-results.csv`; a.click();
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  if (!cycle)    return <p className="p-6 text-red-600">Cycle not found</p>;

  const dist = summary?.distribution;
  const rc   = summary?.review_completion;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <Link to="/cycles" className="text-sm text-blue-600 hover:underline mb-4 inline-block">← Cycles</Link>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{cycle.name}</h1>
          <p className="text-sm text-gray-500 mt-1 capitalize">{cycle.type?.replace('_',' ')} · {cycle.start_date} → {cycle.end_date}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExport} className="btn-secondary btn-sm"><ArrowDownTrayIcon className="w-4 h-4"/>Export CSV</button>
          {cycle.status === 'calibration' && (
            <Link to={`/cycles/${id}/calibration`} className="btn-primary"><ChartBarIcon className="w-4 h-4"/>Open calibration</Link>
          )}
          {cycle.status === 'calibration' && <button onClick={() => lockMut.mutate()} className="btn-secondary">Lock ratings</button>}
          {cycle.status === 'locked'       && <button onClick={() => publishMut.mutate()} className="btn-primary">Publish to employees</button>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard title="Reviews submitted" value={rc ? `${rc.submitted}/${rc.total}` : '—'} color="blue" />
        <StatCard title="Exceptional (X)"   value={dist?.X?.count  ?? '—'} subtitle={dist?.X?.pct  ? `${dist.X.pct}%`  : ''} color="green" />
        <StatCard title="Meets (E)"         value={dist?.E?.count  ?? '—'} subtitle={dist?.E?.pct  ? `${dist.E.pct}%`  : ''} color="purple" />
        <StatCard title="Underperforms (E-)"value={dist?.['E-']?.count ?? '—'} subtitle={dist?.['E-']?.pct ? `${dist['E-'].pct}%` : ''} color="red" />
      </div>

      {dist && (
        <div className="card p-5 mb-6">
          <h2 className="section-title">Band distribution</h2>
          <div className="flex gap-4 flex-wrap">
            {['X','E+','E','E-'].map(band => dist[band] && (
              <div key={band} className="flex items-center gap-3">
                <BandBadge band={band} />
                <span className="text-sm font-semibold text-gray-700">{dist[band].count}</span>
                <span className="text-xs text-gray-400">({dist[band].pct}%)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card p-5">
        <h2 className="section-title">Cycle details</h2>
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            ['Status', cycle.status],
            ['Type', cycle.type?.replace('_',' ')],
            ['Company growth', cycle.company_growth_pct != null ? `${cycle.company_growth_pct}%` : '—'],
            ['Review deadline', cycle.review_deadline || '—'],
            ['Calibration deadline', cycle.calibration_deadline || '—'],
            ['Config version', cycle.config_version],
          ].map(([k,v]) => (
            <div key={k}>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{k}</dt>
              <dd className="mt-1 text-sm font-medium text-gray-900 capitalize">{v}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
